/**
 * @fileoverview 复习反馈面板组件
 * @module ReviewFeedbackPanel
 * @description 集成词汇反馈卡片到句子展示界面，实现完整的复习反馈闭环
 */

import { useEffect, useState } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { SentenceDisplay } from './SentenceDisplay';
import { 
  Send,
  ArrowLeft,
  X
} from 'lucide-react';
import type { GeneratedItem, WordExplanation } from '@ai-voca/shared';
import { wordApi } from '../utils/api';

// 受限白名单渲染器：仅渲染少量安全标签，提升可读性
function RichText({ html, className = '' }: { html: string; className?: string }) {
  const allowed = new Set(['UL', 'OL', 'LI', 'STRONG', 'EM', 'B', 'I', 'BR', 'P']);

  const renderNode = (node: Node, idx: number): any => {
    if (node.nodeType === Node.TEXT_NODE) {
      return (node.textContent || '').replace(/\s+/g, ' ');
    }
    if (node.nodeType === Node.ELEMENT_NODE) {
      const el = node as HTMLElement;
      const children = Array.from(el.childNodes).map((n, i) => renderNode(n, i));
      if (!allowed.has(el.tagName)) {
        return children;
      }
      switch (el.tagName) {
        case 'UL':
          return <ul key={idx} className="list-disc pl-4 space-y-1">{children}</ul>;
        case 'OL':
          return <ol key={idx} className="list-decimal pl-4 space-y-1">{children}</ol>;
        case 'LI':
          return <li key={idx} className="leading-snug">{children}</li>;
        case 'STRONG':
        case 'B':
          return <strong key={idx}>{children}</strong>;
        case 'EM':
        case 'I':
          return <em key={idx}>{children}</em>;
        case 'BR':
          return <br key={idx} />;
        case 'P':
          return <p key={idx} className="leading-snug">{children}</p>;
        default:
          return children;
      }
    }
    return null;
  };

  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(`<div>${html}</div>`, 'text/html');
    const container = doc.body.firstChild as HTMLElement | null;
    const nodes = container ? Array.from(container.childNodes).map((n, i) => renderNode(n, i)) : html;
    return <div className={className}>{nodes}</div>;
  } catch {
    return <div className={className}>{html}</div>;
  }
}

// 从 definition 中剔除“同义/反义/词源/记忆”冗余段，交由专属区域展示
function cleanupDefinition(raw?: string): string | undefined {
  if (!raw) return raw;
  const markers = ['同义：', '反义：', '词源：', '记忆：'];
  const positions = markers
    .map((m) => raw.indexOf(m))
    .filter((i) => i >= 0);
  if (positions.length === 0) return raw;
  const cut = Math.min(...positions);
  return raw.slice(0, cut).trim();
}

type Rating = 'again' | 'hard' | 'good' | 'easy';

interface ReviewFeedbackPanelProps {
  /** 当前句子数据 */
  item: GeneratedItem;
  /** 句子索引 */
  currentIndex: number;
  /** 总句子数量 */
  totalSentences: number;
  /** 是否正在提交 */
  isSubmitting?: boolean;
  /** 提交反馈 */
  onSubmitFeedback: (feedback: ReviewFeedback) => void;
  /** 下一句 */
  onNextSentence: () => void;
  /** 上一句 */
  onPreviousSentence?: () => void;
  /** 退出本次练习 */
  onExit?: () => void;
}

export interface ReviewFeedback {
  /** 词汇反馈 */
  wordFeedback: Record<string, Rating>;
  /** 整体难度反馈 */
  difficultyFeedback: 'too_easy' | 'ok' | 'too_hard' | null;
  /** 句子ID */
  sentenceId: string;
  /** 目标词汇 */
  targets: string[];
}

interface DifficultyFeedbackProps {
  feedback: 'too_easy' | 'ok' | 'too_hard' | null;
  onFeedback: (feedback: 'too_easy' | 'ok' | 'too_hard') => void;
}

/**
 * 整体难度反馈组件
 */
function DifficultyFeedback({ feedback, onFeedback, compact = false }: DifficultyFeedbackProps & { compact?: boolean }) {
  const options = [
    { value: 'too_easy' as const, label: '太简单' },
    { value: 'ok' as const, label: '合适' },
    { value: 'too_hard' as const, label: '太难' }
  ];

  return (
    <div className={compact ? 'space-y-2' : 'space-y-4'}>
      {!compact && (
        <div className="text-center">
          <h3 className="text-lg font-semibold text-foreground mb-2">整体难度反馈</h3>
          <p className="text-sm text-muted-foreground">您觉得这句话的整体难度如何？</p>
        </div>
      )}

      <div className={compact ? 'grid grid-cols-3 gap-1' : 'grid grid-cols-3 gap-3'}>
        {options.map((option) => (
          <Button
            key={option.value}
            variant={feedback === option.value ? 'default' : 'outline'}
            size="sm"
            className={`flex items-center justify-center ${compact ? 'h-7 px-2 text-xs' : 'h-9 px-4 text-sm'} font-medium transition-all duration-200 ${
              feedback === option.value ? 'ring-2 ring-primary shadow-lg bg-primary text-primary-foreground' : 'hover:scale-105'
            }`}
            onClick={() => onFeedback(option.value)}
          >
            {option.label}
          </Button>
        ))}
      </div>
    </div>
  );
}

/**
 * 紧凑的词汇反馈组件
 */
function CompactWordFeedback({
  word,
  feedback,
  onFeedback,
  disabled,
  details,
  detailsLoading
}: {
  word: string;
  feedback: Rating | undefined;
  onFeedback: (rating: Rating) => void;
  disabled?: boolean;
  details?: WordExplanation;
  detailsLoading?: boolean;
}) {
  const ratingOptions = [
    { value: 'again' as const, label: '完全不记得' },
    { value: 'hard' as const, label: '需要提示' },
    { value: 'good' as const, label: '基本掌握' },
    { value: 'easy' as const, label: '熟练掌握' }
  ];

  const cleanedDefinition = cleanupDefinition(details?.definition);

  return (
    <div className="py-2 px-3 bg-muted/30 rounded-lg hover:bg-muted/50 transition-colors">
      <div className="flex items-center justify-between gap-2">
        <span className="font-medium text-sm whitespace-nowrap">{word}</span>
        <div className="flex gap-1 flex-wrap justify-end">
          {ratingOptions.map((option) => (
            <Button
              key={option.value}
              variant={feedback === option.value ? 'default' : 'ghost'}
              size="sm"
              className={`h-7 px-3 text-xs transition-all ${
                feedback === option.value
                  ? 'ring-1 ring-primary shadow-sm bg-primary text-primary-foreground'
                  : 'hover:bg-background border'
              }`}
              onClick={() => onFeedback(option.value)}
              disabled={disabled}
            >
              {option.label}
            </Button>
          ))}
        </div>
      </div>

      {feedback !== undefined && (
        <div className="mt-2 pl-1 text-xs text-muted-foreground space-y-1">
          {detailsLoading && (
            <div className="opacity-80">加载中…</div>
          )}
          {details && (
            <div className="space-y-1">
              {cleanedDefinition && (
                <div>
                  <span className="font-medium mr-1">释义：</span>
                  <RichText html={cleanedDefinition} className="leading-snug inline-block align-top" />
                </div>
              )}

              {Array.isArray(details.synonyms) && details.synonyms.length > 0 && (
                <div className="flex items-start gap-1 flex-wrap">
                  <span className="font-medium">同义：</span>
                  <div className="flex gap-1 flex-wrap">
                    {details.synonyms.slice(0, 6).map((s) => (
                      <span key={s} className="rounded bg-muted px-1 py-0.5 text-[11px] text-foreground/90">
                        {s}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {Array.isArray(details.antonyms) && details.antonyms.length > 0 && (
                <div className="flex items-start gap-1 flex-wrap">
                  <span className="font-medium">反义：</span>
                  <div className="flex gap-1 flex-wrap">
                    {details.antonyms.slice(0, 6).map((s) => (
                      <span key={s} className="rounded bg-muted px-1 py-0.5 text-[11px] text-foreground/90">
                        {s}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {details.etymology && (
                <div>
                  <span className="font-medium mr-1">词源：</span>
                  <RichText html={details.etymology.length > 180 ? `${details.etymology.slice(0, 180)}…` : details.etymology} className="leading-snug inline" />
                </div>
              )}

              {details.memoryTips && (
                <div>
                  <span className="font-medium mr-1">记忆：</span>
                  <RichText html={details.memoryTips.length > 180 ? `${details.memoryTips.slice(0, 180)}…` : details.memoryTips} className="leading-snug inline" />
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * 复习反馈面板
 */
export function ReviewFeedbackPanel({
  item,
  currentIndex,
  totalSentences,
  isSubmitting = false,
  onSubmitFeedback,
  // onNextSentence,
  onPreviousSentence,
  onExit
}: ReviewFeedbackPanelProps) {
  const [wordFeedback, setWordFeedback] = useState<Record<string, Rating>>({});
  const [difficultyFeedback, setDifficultyFeedback] = useState<'too_easy' | 'ok' | 'too_hard' | null>(null);
  const [wordDetails, setWordDetails] = useState<Record<string, { loading: boolean; data?: WordExplanation }>>({});

  // 获取当前句子的目标词汇
  const targetWords = item.targets.map(target => target.word);

  // 当句子切换时重置本地反馈状态（使用索引和文本作为变化信号，避免 sid 不变导致未重置）
  useEffect(() => {
    setWordFeedback({});
    setDifficultyFeedback(null);
  }, [currentIndex, item.text]);

  // 预取目标词的详细信息（异步后台加载）
  useEffect(() => {
    let cancelled = false;
    // 初始化加载状态
    setWordDetails((prev) => {
      const next = { ...prev };
      targetWords.forEach((w) => {
        if (!next[w]) next[w] = { loading: true };
      });
      return next;
    });

    // 逐个异步查询，减少单点失败影响
    (async () => {
      for (const w of targetWords) {
        try {
          const res = await wordApi.queryWord({ word: w, includeExample: false });
          if (cancelled) return;
          setWordDetails((prev) => ({ ...prev, [w]: { loading: false, data: res.data } }));
        } catch {
          if (cancelled) return;
          setWordDetails((prev) => ({ ...prev, [w]: { loading: false } }));
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [currentIndex, item.text]);

  // 防御：若用户切到下一句后，仍然点击旧句子的提交按钮，强制检查目标词集合一致
  const targetSet = new Set(item.targets.map(t => t.word));
  const sanitizedFeedback = Object.fromEntries(
    Object.entries(wordFeedback).filter(([w]) => targetSet.has(w))
  );

  // 计算完成度（仅要求当前句子的目标词都已评分，不限制额外键）
  const isWordFeedbackComplete = targetWords.every(word => sanitizedFeedback[word] !== undefined);
  const isDifficultyFeedbackComplete = difficultyFeedback !== null;
  const isFeedbackComplete = isWordFeedbackComplete && isDifficultyFeedbackComplete;



  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* 顶部导航 */}
      <div className="sticky top-0 z-10 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-b border-border/40">
        <div className="relative flex items-center px-4 py-3">
          {/* 左侧：退出 + 上一句 */}
          <div className="flex items-center space-x-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onExit && onExit()}
              className="flex items-center space-x-1"
              aria-label="退出练习"
            >
              <X className="w-4 h-4" />
            </Button>
            {onPreviousSentence && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onPreviousSentence}
                disabled={currentIndex === 0}
                className="flex items-center space-x-1"
              >
                <ArrowLeft className="w-4 h-4" />
                <span>上一句</span>
              </Button>
            )}
          </div>

          {/* 居中标题：绝对定位保证视觉居中，不受左侧宽度影响 */}
          <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
            <div className="text-center">
              <h1 className="text-lg font-semibold">词汇复习</h1>
              <p className="text-xs text-muted-foreground">
                句子 {currentIndex + 1} / {totalSentences}
              </p>
            </div>
          </div>

          {/* 右侧占位（如未来需要放更多按钮） */}
          <div className="ml-auto" />
        </div>
      </div>

      {/* 主要内容：移动端单列，桌面端两列布局；仅在桌面端占满剩余高度并内部滚动 */}
      <div className="lg:flex-1 lg:overflow-hidden">
        <div className="p-4 max-w-6xl mx-auto lg:h-full">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:items-stretch lg:h-full">
          {/* 左列：句子展示 */}
          <div className="space-y-4 lg:h-full lg:overflow-visible">
            <SentenceDisplay
              item={item}
              showNewTerms={true}
              expandable={true}
              className="glass hover-lift border-0 shadow-lg lg:h-full flex flex-col"
            />
          </div>

          {/* 右列：合并为单张反馈卡片（词汇反馈 + 整体反馈 + 提交） */}
          <Card className="glass hover-lift border-0 shadow-lg lg:h-full flex flex-col">
            <CardContent className="p-3 sm:p-4 lg:flex-1 flex flex-col lg:overflow-hidden">
              <div className="text-sm font-medium text-foreground mb-2">
                反馈
              </div>

              {/* 内容区域可滚动：词汇反馈列表 */}
              <div className="lg:flex-1 lg:overflow-auto pr-1 space-y-1">
                {targetWords.map((word) => (
                  <CompactWordFeedback
                    key={word}
                    word={word}
                    feedback={wordFeedback[word]}
                    onFeedback={(rating: Rating) => {
                      setWordFeedback(prev => ({
                        ...prev,
                        [word]: rating
                      }));
                    }}
                    disabled={isSubmitting}
                    details={wordDetails[word]?.data}
                    detailsLoading={wordDetails[word]?.loading}
                  />
                ))}
              </div>

              {/* 紧凑的整体难度反馈 */}
              <div className="mt-3">
                <DifficultyFeedback
                  feedback={difficultyFeedback}
                  onFeedback={setDifficultyFeedback}
                  compact
                />
              </div>

              {/* 底部提交区域 */}
              <div className="pt-3 mt-auto">
                <div className="flex justify-end">
                  <Button
                    size="lg"
                    onClick={() => {
                      if (isFeedbackComplete) {
                        onSubmitFeedback({
                          wordFeedback,
                          difficultyFeedback,
                          sentenceId: item.sid,
                          targets: targetWords
                        });
                      }
                    }}
                    disabled={!isFeedbackComplete || isSubmitting}
                    className="w-full flex items-center justify-center gap-2 hover-scale transition-all duration-300 shadow-lg"
                  >
                  {isSubmitting ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      提交中...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      提交
                    </>
                  )}
                  </Button>
                </div>
                {/* 提示信息已移除 */}
              </div>
            </CardContent>
          </Card>
          </div>
        </div>
      </div>
    </div>
  );
}